import { ref } from '@vue/composition-api'
import { PrinterConnection } from '@yonarezi/yonarezi3.epos-printer'
import useCurrentData from '@/views/composable/useCurrentData'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'

const MAX_RETRIES = 3
const RETRY_INTERVAL_MS = 2000
const RETRY_CYCLE_COOLDOWN_MS = 30000
const SDK_RECONNECT_WAIT_MS = 5000

export default () => {
  const { currentClub } = useCurrentData()

  const isConnected = ref(null)
  const isConnecting = ref(null)
  const printer = ref(null)

  let reconnectTimer = null
  let resolvedIpAddress = null

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const retryConnect = async attempt => {
    // SDK再接続で既に復帰済み
    if (isConnected.value) return

    if (attempt >= MAX_RETRIES) {
      console.warn(`[ReceiptPrinter] ${MAX_RETRIES}回の再接続に失敗しました。${RETRY_CYCLE_COOLDOWN_MS / 1000}秒後に再試行します`)
      reconnectTimer = setTimeout(() => {
        retryConnect(0)
      }, RETRY_CYCLE_COOLDOWN_MS)

      return
    }

    console.info(`[ReceiptPrinter] 再接続試行 ${attempt + 1}/${MAX_RETRIES}`)
    isConnecting.value = true

    try {
      const printerConnection = new PrinterConnection({ ipAddress: resolvedIpAddress })
      const newPrinter = await printerConnection.connect()

      printer.value = newPrinter
      isConnected.value = !!newPrinter.ePosDev.isConnected()
      isConnecting.value = false
      // eslint-disable-next-line no-use-before-define
      setupEventListeners(newPrinter)
      console.info('[ReceiptPrinter] 再接続に成功しました')
    } catch (error) {
      console.error(`[ReceiptPrinter] 再接続試行 ${attempt + 1} 失敗:`, error)
      isConnecting.value = false
      reconnectTimer = setTimeout(() => {
        retryConnect(attempt + 1)
      }, RETRY_INTERVAL_MS)
    }
  }

  const startReconnect = () => {
    clearReconnectTimer()

    // SDKの内部再接続を待ってから独自リトライを開始
    reconnectTimer = setTimeout(() => {
      retryConnect(0)
    }, SDK_RECONNECT_WAIT_MS)
  }

  const setupEventListeners = printerObj => {
    const { ePosDev } = printerObj

    ePosDev.ondisconnect = () => {
      console.warn('[ReceiptPrinter] 切断を検知しました。再接続を試みます...')
      isConnected.value = false
      isConnecting.value = false
      startReconnect()
    }

    ePosDev.onreconnecting = () => {
      console.info('[ReceiptPrinter] SDK再接続中...')
      isConnecting.value = true
    }

    ePosDev.onreconnect = () => {
      console.info('[ReceiptPrinter] SDK再接続に成功しました')
      isConnected.value = true
      isConnecting.value = false
      clearReconnectTimer()
    }
  }

  const checkConnection = async (printIpAddress = null) => {
    isConnecting.value = true

    let ipAddress = printIpAddress
    if (!ipAddress) {
      ipAddress = currentClub.value.printIpAddress
    }
    ipAddress = removePortFromIpAddress(ipAddress)
    resolvedIpAddress = ipAddress

    try {
      const printerConnection = new PrinterConnection({ ipAddress })
      printer.value = await printerConnection.connect()
      isConnected.value = !!printer.value.ePosDev.isConnected()
      setupEventListeners(printer.value)
    } catch (error) {
      console.error('[ReceiptPrinter] 初回接続に失敗しました:', error)
    } finally {
      isConnecting.value = false
    }
  }

  const cleanup = () => {
    clearReconnectTimer()
  }

  return {
    // data
    isConnected,
    isConnecting,
    printer,

    // methods
    checkConnection,
    cleanup,
  }
}
